<template>
	<div ref="el" data-component="photo-spiral-grid">
		<div class="inner">
			<div v-for="(item, i) in photos" :key="i" class="photo-container">
				<ImageAsset v-if="item" :asset="item" sizes="xsmall:200px medium:1200px xlarge:1700px" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'
import.meta.client && gsap.registerPlugin(ScrollTrigger)

import { useScreen } from 'vue-screen'
import { breakpoints } from '~/config'
import { ImageAsset } from '~/components/core'

const props = defineProps({
	photos: { type: Array, default: () => [] },
})

const el = ref(null)
const context = inject('pageContext')
const scroller = inject('$scroller')
const tl = gsap.timeline({ paused: true })
const screen = useScreen()
const radius = ref(200)
let observer
let scrollDelta = 0
let canAddDelta = false

const set = (value) => tl.progress(value)
const play = () => tl.play()
const pause = () => tl.pause()

watch(screen, () => {
	kill()

	nextTick(() => setup())
})

const setup = () => {
	const cardList = el.value.querySelectorAll('.photo-container')
	const count = cardList.length
	radius.value = screen.width > breakpoints.small ? screen.height / 2.4 : Math.max(screen.width / 3, 180)
	const sliceAngle = (2 * Math.PI) / count

	gsap.set(cardList, {
		x: (index) => {
			return Math.round(radius.value * Math.cos(sliceAngle * index - Math.PI / 4))
		},
		y: (index) => {
			return Math.round(radius.value * Math.sin(sliceAngle * index - Math.PI / 4))
		},
		rotation: (index) => {
			return (index + 1) * (360 / count)
		},
	})

	gsap.set(cardList, {
		opacity: 0,
		scale: 0,
		x: 0,
		y: 0,
		duration: 2,
	})

	tl.to(cardList, {
		stagger: 0.15,
		opacity: 1,
		scale: 1,
		duration: 1,
		ease: 'power2.inOut',
		x: (index) => {
			return Math.round(radius.value * Math.cos(sliceAngle * index - Math.PI / 4))
		},
		y: (index) => {
			return Math.round(radius.value * Math.sin(sliceAngle * index - Math.PI / 4))
		},
		rotation: (index) => {
			return (index + 1) * (360 / count)
		},
	})
		.to(
			el.value.querySelector('.inner'),
			{
				rotation: -360 - 90,
				duration: 3,
				ease: 'power4.out',
			},
			0,
		)
		.to(
			el.value,
			{
				rotation: '-=360',
				duration: 20,
				ease: 'none',
				repeat: -1,
				onStart: () => {
					setTimeout(() => (canAddDelta = true), 2000)
				},
			},
			0,
		)

	observer = ScrollTrigger.create({
		trigger: el.value,
		start: 'top bottom',
		end: 'bottom top',
		onUpdate: () => {
			const value = 1 + scrollDelta
			canAddDelta && tl.timeScale(value)
		},
		onEnter: () => {
			tl.play()
		},
		onEnterBack: () => {
			tl.play()
		},
		onLeaveBack: () => {
			tl.timeScale(1)
			tl.pause()
			scrollDelta = 0
		},
		onLeave: () => {
			tl.timeScale(1)
			tl.pause()
			scrollDelta = 0
		},
	})

	screen.width > breakpoints.medium && scroller.value?.getScrollerInstance().value.on('scroll', updateDelta)
}

const updateDelta = ({ velocity }) => (scrollDelta = velocity / 5)

const init = () => {}
const kill = () => {
	const cardList = el.value.querySelectorAll('.photo-container')
	scroller.value?.getScrollerInstance().value.off('scroll', updateDelta)
	gsap.killTweensOf([cardList, el.value, el.value.querySelector('.inner')])
	tl.kill()
}

onBeforeUnmount(() => kill())
onMounted(() => {
	context.$page.loader.transitionReady.then(() => setup())
})

defineExpose({
	set,
	play,
	pause,
})
</script>

<style lang="scss" scoped>
[data-component='photo-spiral-grid'] {
	position: relative;

	height: 100%;
	width: 100%;

	will-change: transform;

	.inner {
		display: grid;
		align-items: center;
		justify-items: center;

		height: 100%;
		width: 100%;
	}

	.photo-container {
		grid-row: 1;
		grid-column: 1;

		width: rs(180);
		border-radius: rs(6);
		overflow: hidden;
	}
}
</style>
